import { useEffect } from "react";
import * as THREE from "three";
import { setTendonVisualizationUpdater } from "../../runtime/tendonVisualizationBridge";
import { useSceneStore } from "../../stores/sceneStore";
import { applyConstraintMarkerKeySelection } from "../scene/constraintMarkerVisibility";
import {
  COINCIDENT_TENDON_ARC_BOW_SCALE,
  buildTendonCurvePoints,
  canonicalJointPairKey,
  jointPairsWithCoincidentTendonAndJointEq,
} from "../scene/constraintMarkerGeometry";
import {
  computeConstraintMarkerScaleFromModelGroup,
  getConstraintMarkerSettingsScales,
} from "../scene/jointMarkerSizing";
import {
  buildViewerSceneRouteObjectIndex,
  collectSpatialTendonRouteObjects,
  findJointVizGroupInScene,
} from "../scene/sceneObjectLookups";
import { TENDON_COLORS, getTendonVisualizationStyle } from "../scene/tendonStyle";
import {
  TENDON_VISUALIZATION_LINE_WIDTH,
  TENDON_VISUALIZATION_MARKER_RADIUS,
} from "../scene/sceneConstants";

export function useTendonVisualization({
  sceneRef,
  rendererRef,
  tendonVisualizationRef,
  constraintMarkersGroupRef,
  constraintPointerRef,
  constraintHoveredKeyRef,
  constraintMarkerSettings,
  tendonMarkerLayerVisible,
  selectedTendon,
}) {
  // Tendon arc visualization
  useEffect(() => {
    if (!sceneRef.current) return;

    let tendonGroup = tendonVisualizationRef.current;
    if (!tendonGroup) {
      tendonGroup = new THREE.Group();
      tendonGroup.name = "tendon_visualization";
      sceneRef.current.add(tendonGroup);
      tendonVisualizationRef.current = tendonGroup;
    }

    const updateTendons = (tendons) => {
      const group = tendonVisualizationRef.current;
      if (!group) return;
      while (group.children.length > 0) group.remove(group.children[0]);
      const allowTendonVisualization =
        tendonMarkerLayerVisible || (typeof selectedTendon === "string" && !!selectedTendon);
      group.visible = allowTendonVisualization;
      if (!allowTendonVisualization || !tendons || tendons.length === 0) return;
      const markerScale = computeConstraintMarkerScaleFromModelGroup(
        sceneRef.current?.getObjectByName("modelGroup")
      );
      const constraintMarkerScales = getConstraintMarkerSettingsScales(constraintMarkerSettings);
      const tendonMarkerRadius =
        TENDON_VISUALIZATION_MARKER_RADIUS * markerScale * constraintMarkerScales.markerScale;
      const tendonLineWidth =
        TENDON_VISUALIZATION_LINE_WIDTH * markerScale * constraintMarkerScales.lineWidthScale;
      const routeObjectIndex = buildViewerSceneRouteObjectIndex(sceneRef.current);

      tendons.forEach((tendon, idx) => {
        if (!tendonMarkerLayerVisible && tendon.uuid !== selectedTendon) return;
        const fallbackColor = TENDON_COLORS[idx % TENDON_COLORS.length];
        const tendonStyle = getTendonVisualizationStyle(
          tendon,
          fallbackColor,
          tendonLineWidth,
          0.85,
          constraintMarkerScales.tendonParsedAppearance
        );
        const color = tendonStyle.color;
        const tendonMarkerKey = `tendon:${tendon.uuid}`;

        if (tendon.kind === "spatial") {
          const routeObjects = collectSpatialTendonRouteObjects(
            sceneRef.current,
            tendon,
            routeObjectIndex
          );
          if (routeObjects.length < 2) return;

          const positions = routeObjects.map((obj) => {
            const pos = new THREE.Vector3();
            obj.getWorldPosition(pos);
            return pos;
          });

          const lineGeo = new THREE.BufferGeometry().setFromPoints(positions);
          const lineMat = new THREE.LineBasicMaterial({
            color,
            transparent: true,
            opacity: tendonStyle.opacity,
            linewidth: tendonStyle.lineWidth,
            depthTest: false,
            depthWrite: false,
          });
          const line = new THREE.Line(lineGeo, lineMat);
          line.renderOrder = 9998;
          line.userData.isConstraintVisualization = true;
          line.userData.isTendonVisualization = true;
          line.userData.key = tendonMarkerKey;
          line.userData.tendonRouteObjects = routeObjects;
          group.add(line);

          routeObjects.forEach((routeObj) => {
            const pos = new THREE.Vector3();
            routeObj.getWorldPosition(pos);
            const sphere = new THREE.Mesh(
              new THREE.SphereGeometry(tendonMarkerRadius, 10, 10),
              new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false })
            );
            sphere.position.copy(pos);
            sphere.renderOrder = 9998;
            sphere.material.visible = constraintMarkerScales.anchorSpheresVisible;
            sphere.userData.isConstraintVisualization = true;
            sphere.userData.isTendonVisualization = true;
            sphere.userData.key = tendonMarkerKey;
            sphere.userData.tendonRouteObject = routeObj;
            group.add(sphere);
          });
          return;
        }

        const jointRefs = (tendon.joints || []).filter((j) => j?.joint);
        if (jointRefs.length < 2) return;

        const memberIds = jointRefs.map((j) => j.joint).filter(Boolean);
        const snap = useSceneStore.getState();
        const coincidentPanel =
          memberIds.length === 2 &&
          jointPairsWithCoincidentTendonAndJointEq(snap.tendons, snap.equality).has(
            canonicalJointPairKey(memberIds[0], memberIds[1])
          );

        const jointData = jointRefs
          .map(({ joint: uuid }) => {
            const vizGroup = findJointVizGroupInScene(sceneRef.current, uuid);
            if (!vizGroup) return null;
            const pos = new THREE.Vector3();
            vizGroup.getWorldPosition(pos);
            return { vizGroup, pos };
          })
          .filter(Boolean);

        if (jointData.length < 2) return;

        const positions = jointData.map((d) => d.pos);
        const vizGroups = jointData.map((d) => d.vizGroup);

        const tendonArcBowScale = coincidentPanel ? COINCIDENT_TENDON_ARC_BOW_SCALE : 1;
        const curvePoints = buildTendonCurvePoints(positions, tendonArcBowScale);
        const curve = new THREE.CatmullRomCurve3(curvePoints);
        const pts = curve.getPoints(48);

        const lineGeo = new THREE.BufferGeometry().setFromPoints(pts);
        const lineMat = new THREE.LineBasicMaterial({
          color,
          transparent: true,
          opacity: tendonStyle.opacity,
          linewidth: tendonStyle.lineWidth,
          depthTest: false,
          depthWrite: false,
        });
        const line = new THREE.Line(lineGeo, lineMat);
        line.renderOrder = 9998;
        line.userData.isConstraintVisualization = true;
        line.userData.isTendonVisualization = true;
        line.userData.key = tendonMarkerKey;
        line.userData.tendonVizGroups = vizGroups;
        line.userData.arcBowScale = tendonArcBowScale;
        group.add(line);

        // Small sphere at each joint endpoint
        vizGroups.forEach((vg) => {
          const pos = new THREE.Vector3();
          vg.getWorldPosition(pos);
          const sphere = new THREE.Mesh(
            new THREE.SphereGeometry(tendonMarkerRadius, 10, 10),
            new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false })
          );
          sphere.position.copy(pos);
          sphere.renderOrder = 9998;
          sphere.material.visible = constraintMarkerScales.anchorSpheresVisible;
          sphere.userData.isConstraintVisualization = true;
          sphere.userData.isTendonVisualization = true;
          sphere.userData.key = tendonMarkerKey;
          sphere.userData.tendonVizGroup = vg;
          group.add(sphere);
        });
      });
    };

    setTendonVisualizationUpdater(updateTendons);

    const _scratchPos = new THREE.Vector3();
    const animateTendons = () => {
      const group = tendonVisualizationRef.current;
      if (group) {
        group.children.forEach((obj) => {
          if (!obj.userData?.isTendonVisualization) return;
          if (obj.userData.tendonVizGroups) {
            // Recompute curve from live joint world positions (fixed tendon)
            const positions = obj.userData.tendonVizGroups.map((vg) => {
              vg.getWorldPosition(_scratchPos);
              return _scratchPos.clone();
            });
            if (positions.length >= 2) {
              const bowScale = obj.userData.arcBowScale ?? 1;
              const curvePoints = buildTendonCurvePoints(positions, bowScale);
              const curve = new THREE.CatmullRomCurve3(curvePoints);
              const pts = curve.getPoints(48);
              obj.geometry.setFromPoints(pts);
              obj.geometry.attributes.position.needsUpdate = true;
            }
          } else if (obj.userData.tendonRouteObjects) {
            // Recompute line from live route object world positions (spatial tendon).
            const positions = obj.userData.tendonRouteObjects.map((routeObj) => {
              routeObj.getWorldPosition(_scratchPos);
              return _scratchPos.clone();
            });
            if (positions.length >= 2) {
              obj.geometry.setFromPoints(positions);
              obj.geometry.attributes.position.needsUpdate = true;
            }
          } else if (obj.userData.tendonVizGroup) {
            obj.userData.tendonVizGroup.getWorldPosition(obj.position);
          } else if (obj.userData.tendonRouteObject) {
            obj.userData.tendonRouteObject.getWorldPosition(obj.position);
          }
        });
      }
      requestAnimationFrame(animateTendons);
    };
    animateTendons();

    return () => {
      setTendonVisualizationUpdater(null);
      if (tendonGroup && tendonGroup.parent) {
        tendonGroup.parent.remove(tendonGroup);
      }
      tendonVisualizationRef.current = null;
    };
  }, [
    sceneRef,
    constraintMarkerSettings,
    tendonMarkerLayerVisible,
    selectedTendon,
    tendonVisualizationRef,
  ]);

  // Pointer NDC tracking + click-to-select for constraint/tendon markers
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      constraintPointerRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      constraintPointerRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const onClick = () => {
      const key = constraintHoveredKeyRef.current;
      if (!key || !constraintMarkersGroupRef.current?.visible) return;
      applyConstraintMarkerKeySelection(key);
    };
    canvas.addEventListener("pointermove", onMove, { passive: true });
    canvas.addEventListener("click", onClick);
    return () => {
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("click", onClick);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
